import { countLabel, perDayLabel } from './facts';
import { num } from './format';

/**
 * The "About Raise Sonar" block: the house panel that tells a visitor who
 * landed on one page what the site covers and does. Its markup is
 * views/partials/about-promo.hbs, its look `.ab-*` in site.css; this is
 * everything it says. At the foot of /raises/{slug}, and built to go on any
 * public page.
 */

/** The figures; SiteFactsService's. */
export interface AboutPromoProps {
  /** Live raises right now. */
  liveCount: number;
  /** Platforms read today, as every page states it: "26". */
  platformCount?: number | null;
  /** The countries they are in: "26 platforms in 12 countries". */
  countryCount?: number | null;
  /** New raises a day, a 30-day average, shown as "~3". */
  newPerDay?: number | null;
  /** The platforms with the most live raises, busiest first: the sonar's blips. */
  busiest?: string[] | null;
  /** The body's examples, before "and more". */
  platformNames?: string[];
  /** Which page it is on, for its event: 'deal_page'. */
  source?: string;
  /** The Raise Sonar app, where the live count links: its feed lists them all. */
  appUrl?: string | null;
}

export const DEFAULT_PLATFORM_NAMES = [
  'Crowdcube',
  'Republic',
  'Wefunder',
  'StartEngine',
];

export const PROMO_HEADING =
  'Every live equity crowdfunding raise, in one place';

/** The four things it can do for the reader, in the brief's order. */
export const PROMO_CAPABILITIES = [
  {
    icon: 'bell',
    title: 'Launch alerts',
    line: 'An email when a raise matching your filters opens.',
  },
  {
    icon: 'clock',
    title: 'Close reminders',
    line: 'Follow a raise and hear from us 72 and 24 hours before it closes.',
  },
  {
    icon: 'search',
    title: 'One search, every platform',
    line: 'Filter live raises by sector, country, minimum and platform.',
  },
  {
    icon: 'mail',
    title: 'The weekly newsletter',
    line: 'New raises and raises closing soon, every Monday.',
  },
] as const;

export type PromoIcon = (typeof PROMO_CAPABILITIES)[number]['icon'];

/**
 * The capabilities' line icons, as path data on a 24×24 grid, drawn with a
 * 1.8 stroke in currentColor (.ab-icon). The site has no icon set, so they
 * are the usual bell, clock, magnifier and envelope, drawn here.
 */
export const PROMO_ICON_PATHS: Record<PromoIcon, string[]> = {
  bell: [
    'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9',
    'M13.7 21a2 2 0 0 1-3.4 0',
  ],
  clock: ['M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z', 'M12 7v5l3 2'],
  search: ['M18 11a7 7 0 1 1-14 0 7 7 0 0 1 14 0z', 'M20.5 20.5 16 16'],
  mail: [
    'M5 5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z',
    'm3 7 9 6 9-6',
  ],
};

export interface PromoStat {
  value: string;
  label: string;
  /** Where the figure links, if anywhere: the live count, to the app's feed. */
  href: string | null;
}

/**
 * Where the sonar's blips sit, busiest platform first: degrees clockwise
 * from the top, and the distance out as a share of the disc's radius.
 */
export const BLIP_POSITIONS = [
  [38, 0.64],
  [96, 0.8],
  [140, 0.42],
  [178, 0.72],
  [228, 0.84],
  [268, 0.5],
  [312, 0.8],
  [350, 0.38],
] as const;

/** One turn of the sweep, in seconds (.ab-sweep's animation). */
export const SWEEP_SECONDS = 6;

export interface Blip {
  name: string;
  /** Across and down the sonar, in percent, from its top left. */
  x: number;
  y: number;
  /** When the sweep first passes it, in seconds: its glow's delay. */
  delay: number;
  /**
   * Near the disc's right edge, where a small sonar puts the name on the
   * dot's left so the panel does not cut it off.
   */
  east: boolean;
}

/** The busiest platforms, each at its position on the sonar. */
export function blipsOf(names: readonly string[] | null | undefined): Blip[] {
  const round = (n: number) => Math.round(n * 100) / 100;
  return (names ?? []).slice(0, BLIP_POSITIONS.length).map((name, i) => {
    const [angle, out] = BLIP_POSITIONS[i];
    const rad = (angle * Math.PI) / 180;
    const x = round(50 + 50 * out * Math.sin(rad));
    return {
      name,
      x,
      y: round(50 - 50 * out * Math.cos(rad)),
      delay: round((angle / 360) * SWEEP_SECONDS),
      east: x > 75,
    };
  });
}

export interface AboutPromoView {
  source: string;
  heading: string;
  body: string;
  stats: PromoStat[];
  /** The sonar's centre: the live count. */
  live: string;
  blips: Blip[];
  capabilities: {
    icon: PromoIcon;
    title: string;
    line: string;
    paths: string[];
  }[];
}

/**
 * The stats row. A figure that is missing, or that would print as 0 (no
 * platforms, under half a raise a day), is left out with its divider, never
 * shown as "0" or a dash. So is the live count where it could not be read,
 * for the /raises hero; the block itself is drawn only with one. Every
 * figure is the one the rest of the site states (site/facts.ts).
 */
export function promoStats(props: {
  liveCount: number | null;
  platformCount?: number | null;
  countryCount?: number | null;
  newPerDay?: number | null;
  appUrl?: string | null;
}): PromoStat[] {
  const stats: PromoStat[] = [];
  if (props.liveCount !== null && Number.isFinite(props.liveCount)) {
    stats.push({
      value: num(props.liveCount),
      label: 'live raises now',
      href: props.appUrl ?? null,
    });
  }
  const platforms = countLabel(props.platformCount ?? null);
  if (platforms !== null && (props.platformCount ?? 0) >= 1) {
    const countries = props.countryCount ?? null;
    const noun = props.platformCount === 1 ? 'platform' : 'platforms';
    stats.push({
      value: platforms,
      label:
        countries !== null && countries >= 1
          ? `${noun} in ${countLabel(countries)} ${countries === 1 ? 'country' : 'countries'}`
          : `${noun} tracked`,
      href: null,
    });
  }
  const perDay = perDayLabel(props.newPerDay ?? null);
  if (perDay !== null) {
    stats.push({ value: perDay, label: 'new raises a day', href: null });
  }
  return stats;
}

/** "We track raises across Crowdcube, Republic, Wefunder, StartEngine and more, …" */
export function promoBody(names: readonly string[] | undefined): string {
  const list = names?.map((n) => n.trim()).filter(Boolean);
  const shown = list?.length ? list : DEFAULT_PLATFORM_NAMES;
  return `We track raises across ${shown.join(', ')} and more, and tell you when they open and before they close.`;
}

export function aboutPromoView(props: AboutPromoProps): AboutPromoView {
  return {
    source: props.source ?? 'site',
    heading: PROMO_HEADING,
    body: promoBody(props.platformNames),
    stats: promoStats(props),
    live: num(props.liveCount),
    blips: blipsOf(props.busiest),
    capabilities: PROMO_CAPABILITIES.map((c) => ({
      ...c,
      paths: PROMO_ICON_PATHS[c.icon],
    })),
  };
}
